import React, { createContext, useContext, useState, useEffect } from "react";
import type { User } from "../types/user";
import { 
  loginApi, 
  signupApi, 
  googleLoginApi, 
  fetchCurrentUserApi 
} from "../api/auth";

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  mockGoogleLogin: (email: string, name: string) => Promise<void>; // Developer mock helper
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Initialize and verify existing session
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("wesi_auth_token");
      const storedUser = localStorage.getItem("wesi_auth_user");

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));

          // Verify token against backend
          const verifiedUser = await fetchCurrentUserApi(storedToken);
          setUser(verifiedUser);
          localStorage.setItem("wesi_auth_user", JSON.stringify(verifiedUser));
        } catch (error: any) {
          console.error("Auth verification failed", error);
          // Log out if verification explicitly rejected (e.g. token expired) 
          // but preserve session on network dropouts (Failed to fetch)
          if (error.message && !error.message.includes("Failed to fetch")) {
            handleLogout();
          }
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const handleAuthResponse = (data: { token: string; user: User }) => {
    setToken(data.token);
    setUser(data.user);
    setAuthError(null);
    localStorage.setItem("wesi_auth_token", data.token);
    localStorage.setItem("wesi_auth_user", JSON.stringify(data.user));
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    setAuthError(null);
    localStorage.removeItem("wesi_auth_token");
    localStorage.removeItem("wesi_auth_user");
  };

  const login = async (email: string, password: string) => {
    setAuthError(null);
    try {
      const data = await loginApi(email, password);
      handleAuthResponse(data);
      localStorage.removeItem("wesi_quiz_industry");
      localStorage.removeItem("wesi_quiz_challenges");
    } catch (error: any) {
      setAuthError(error.message || "Something went wrong during login");
      throw error;
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    setAuthError(null);
    try {
      const quizIndustry = localStorage.getItem("wesi_quiz_industry") || undefined;
      const quizChallenges = localStorage.getItem("wesi_quiz_challenges") || undefined;

      const data = await signupApi(email, password, name, quizIndustry, quizChallenges);
      handleAuthResponse(data);

      localStorage.removeItem("wesi_quiz_industry");
      localStorage.removeItem("wesi_quiz_challenges");
    } catch (error: any) {
      setAuthError(error.message || "Something went wrong during signup");
      throw error;
    }
  };

  const googleLogin = async (credential: string) => {
    setAuthError(null);
    try {
      const quizIndustry = localStorage.getItem("wesi_quiz_industry") || undefined;
      const quizChallenges = localStorage.getItem("wesi_quiz_challenges") || undefined;

      const data = await googleLoginApi(credential, quizIndustry, quizChallenges);
      handleAuthResponse(data);

      localStorage.removeItem("wesi_quiz_industry");
      localStorage.removeItem("wesi_quiz_challenges");
    } catch (error: any) {
      setAuthError(error.message || "Google Sign-In failed");
      throw error;
    }
  };

  // Mock Developer sign-in to bypass Google OAuth requirements in development
  const mockGoogleLogin = async (email: string, name: string) => {
    setAuthError(null);
    try {
      // Simulate registering / logging in via a developer bypass endpoint, 
      // or directly create a mock session if needed. 
      // Here we will use a mock token generated for dev purposes or just register directly.
      const mockCredential = `mock_google_token_${btoa(JSON.stringify({ email, name, sub: "mock_google_" + Date.now() }))}`;
      
      // Let's call the backend signup with a mock password, or mock google endpoint if it handles it.
      // Wait, since google login backend verifies against Google API, a mock token will fail validation.
      // So let's simulate the mock auth response locally directly or register them with email/mock password!
      // To keep it 100% functional with the backend, we can sign them up/login using Email/Password 
      // with a mock password `mock-google-password-123` behind the scenes!
      // This is brilliant! It ensures a real database user is created and backend JWT works!
      const mockPassword = "mock-google-password-123";
      
      try {
        // Try signup first
        await signup(email, mockPassword, name);
      } catch (err: any) {
        // If user already exists, try logging in
        if (err.message.includes("already registered") || err.message.includes("Conflict")) {
          await login(email, mockPassword);
        } else {
          throw err;
        }
      }
    } catch (error: any) {
      setAuthError("Mock login failed: " + error.message);
      throw error;
    }
  };

  const clearError = () => setAuthError(null);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        authError,
        login,
        signup,
        googleLogin,
        mockGoogleLogin,
        logout: handleLogout,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
